import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DATE_PARTS, getWidgetMeta, GEO_TYPES, DRILLDOWN_TYPES, IMAGE_TYPES, GL_TYPES, HIGHLIGHT_TYPES, normalizeDatePart } from "@/lib/widgetCatalog";
import { Plus, X, Trash2, Layers, Settings2, MousePointerClick, SquareDashedMousePointer, TrendingUp, RotateCcw, Accessibility } from "lucide-react";
import { Section, StyleToggle } from "@/components/bi/InspectorBits";
import { InspectorGeneralSection, InspectorLayoutSection, InspectorTypographySection } from "@/components/bi/InspectorGeneral";
import { InspectorDataSection } from "@/components/bi/InspectorData";
import { InspectorContainerSection } from "@/components/bi/InspectorContainer";
import { autoFieldMap, widgetUsedFields, remapWidgetDataset } from "@/lib/builderOps";
import {
  InspectorBackgroundSection, InspectorColorsSection, InspectorLabelsSection, InspectorLegendSection,
  InspectorAxesSection, InspectorTooltipSection, InspectorChartStyleSection,
  InspectorNumberFormatSection, InspectorCondFormatSection,
} from "@/components/bi/InspectorFormat";
import { ActionEditor } from "@/components/bi/ActionEditor";
import { ADV_COLOR_TYPES } from "@/components/bi/FormatEditors";
import { DrillthroughEditor } from "@/components/bi/DrillthroughEditor";
import { DateHierarchyButton } from "@/components/bi/ChartStyleEditors";
import { CARTESIAN_TYPES } from "@/lib/chartStyle";
import { getWells, effectiveType, isNumericType, isDateType, defaultAggFor, readFieldDrag, FIELD_MIME } from "@/lib/fieldWells";
import { toast } from "sonner";

const NO_STYLE = ["text", "table", "slicer", "kpi", "pivot", "perspective", "image", "multi_filter", "data_grid", "container"];

// Item 188: standardized 14-section properties structure —
// Assigned Data + General, Layout, Typography, Background, Colors, Data Labels,
// Legend, Axes, Tooltip, Visual Options, Number Format, Conditional Formatting,
// Interactions & Accessibility, Advanced.
export function Inspector({ widget, multiCount = 0, datasets, sourceDatasets, widgets = [], onUpdate, onDelete, onReleaseChild, onReorderChildren, onChildLayout, onUpdateWidget, cols = 12, dashboardFilters = [] }) {
  const [model, setModel] = useState(null);
  const datasetId = widget?.dataset_id;
  const wtype = widget?.type;
  useEffect(() => {
    let active = true;
    setModel(null);
    if (!datasetId || ["text", "image", "container"].includes(wtype)) return;
    api.get(`/datasets/${datasetId}/model`).then(({ data }) => { if (active) setModel(data); }).catch(() => { if (active) setModel(null); });
    return () => { active = false; };
  }, [datasetId, wtype]);

  if (multiCount > 1) {
    return (
      <div data-testid="inspector-multiselect" className="p-6 text-sm text-[color:var(--ink-3)] text-center space-y-2">
        <SquareDashedMousePointer className="w-6 h-6 mx-auto opacity-60" />
        <div className="font-medium text-[color:var(--ink-2)]">{multiCount} widgets selected</div>
        <p className="text-xs">Use the toolbar above the canvas to align, distribute, group or bulk-format the selection.</p>
      </div>
    );
  }
  if (!widget) {
    return <div className="p-6 text-sm text-[color:var(--ink-3)] text-center">Select a widget to configure it, or add one from the library.</div>;
  }
  const ds = datasets.find((d) => d.id === widget.dataset_id);
  const schema = ds?.schema || [];
  const fmeta = model?.field_meta || {};
  const disp = (f) => fmeta[f]?.display_name || f;
  const notHidden = (name) => !fmeta[name]?.hidden;
  const effType = (name) => effectiveType(name, schema, fmeta);
  const dims = schema.filter((c) => notHidden(c.name) && (c.role === "dimension" || !isNumericType(effType(c.name))));
  const measureFields = schema.filter((c) => notHidden(c.name));
  const config = widget.config || {};
  const opts = config.options || {};
  const meta = getWidgetMeta(widget.type);
  const wells = getWells(widget.type);
  const isGeo = GEO_TYPES.includes(widget.type);
  const isPointMap = widget.type === "point_map" || widget.type === "google_map";
  const isGoogleMap = widget.type === "google_map";
  const isImage = IMAGE_TYPES.includes(widget.type);
  const is3d = GL_TYPES.includes(widget.type);
  const numericFields = schema.filter((c) => notHidden(c.name) && isNumericType(effType(c.name)));
  const hasAxisEditor = CARTESIAN_TYPES.includes(widget.type) || widget.type === "scatter";

  const patch = (p) => onUpdate(widget.id, p);
  const patchConfig = (c) => onUpdate(widget.id, { config: { ...config, ...c } });
  const patchOpts = (o) => patchConfig({ options: { ...opts, ...o } });

  // ---- Governed (semantic) model wiring ----
  const calcDefs = (ds?.calc_fields || []).filter((f) => schema.some((c) => c.name === f.name) && notHidden(f.name));
  const calcFieldMeas = calcDefs.filter((f) => isNumericType(effType(f.name)))
    .map((f) => ({ key: `__field__:${f.name}`, label: disp(f.name), field: f.name, kind: "calculated", agg: defaultAggFor(f.name, schema, fmeta) }));
  const calcFieldDims = calcDefs.filter((f) => !isNumericType(effType(f.name)))
    .map((f) => ({ key: `__field__:${f.name}`, label: disp(f.name), field: f.name, kind: "calculated" }));
  const govDims = [...(model?.dimensions || []).filter((d) => d.visible !== false), ...calcFieldDims];
  const govMeas = [...(model?.measures || []).filter((m) => m.visible !== false), ...calcFieldMeas];
  const FORCE_ADHOC = ["boxplot", "candlestick"];
  const canGovern = !!model && (model?.measures || []).length > 0 && !FORCE_ADHOC.includes(widget.type);
  const governed = canGovern && config.adhoc !== true;
  const dimByKey = Object.fromEntries(govDims.map((d) => [d.key, d]));
  const measByKey = Object.fromEntries(govMeas.map((m) => [m.key, m]));
  const dimByField = {};
  govDims.forEach((d) => { if (!(d.field in dimByField)) dimByField[d.field] = d; });

  const addDimension = () => {
    if (governed) { const d0 = govDims[0]; patchConfig({ dimensions: [...(config.dimensions || []), d0 ? { key: d0.key, field: d0.field, date_part: "none" } : { field: "", date_part: "none" }] }); }
    else patchConfig({ dimensions: [...(config.dimensions || []), { field: dims[0]?.name || "", date_part: "none" }] });
  };
  const updateDimension = (i, k, v) => { const arr = [...(config.dimensions || [])]; arr[i] = { ...arr[i], [k]: v }; patchConfig({ dimensions: arr }); };
  const updateDimensionKey = (i, key) => {
    const dd = dimByKey[key]; const arr = [...(config.dimensions || [])];
    if (key.startsWith("__field__:")) arr[i] = { field: dd?.field || key.slice(10), date_part: arr[i]?.date_part || "none" };
    else arr[i] = { ...arr[i], key, field: dd?.field || arr[i].field };
    patchConfig({ dimensions: arr });
  };
  const removeDimension = (i) => { const arr = [...(config.dimensions || [])]; arr.splice(i, 1); patchConfig({ dimensions: arr }); };

  const addMeasure = () => {
    if (governed) { const m0 = govMeas[0]; patchConfig({ measures: [...(config.measures || []), m0 ? { key: m0.key, field: m0.field || "", agg: m0.agg || "sum" } : {}] }); }
    else { const f0 = measureFields[0]?.name || ""; patchConfig({ measures: [...(config.measures || []), { field: f0, agg: defaultAggFor(f0, schema, fmeta) }] }); }
  };
  const updateMeasure = (i, k, v) => { const arr = [...(config.measures || [])]; arr[i] = { ...arr[i], [k]: v }; patchConfig({ measures: arr }); };
  const updateMeasureKey = (i, key) => {
    const mm = measByKey[key]; const arr = [...(config.measures || [])];
    if (key.startsWith("__field__:")) arr[i] = { field: mm?.field || key.slice(10), agg: mm?.agg || "sum" };
    else arr[i] = { key, field: mm?.field || "", agg: mm?.agg || "sum" };
    patchConfig({ measures: arr });
  };
  const removeMeasure = (i) => { const arr = [...(config.measures || [])]; arr.splice(i, 1); patchConfig({ measures: arr }); };

  const setGovernedMode = (useGov) => {
    if (!useGov) {
      const measures = (config.measures || []).map((m) => ({ field: m.field, agg: m.agg || "sum" })).filter((m) => m.field);
      const dimensions = (config.dimensions || []).map((d) => ({ field: d.field, date_part: d.date_part || "none" })).filter((d) => d.field);
      patchConfig({ adhoc: true, measures, dimensions });
    } else {
      const measures = (config.measures || []).map((m) => {
        if (m.key) return m;
        const gm = govMeas.find((x) => x.field === m.field && (x.agg || "sum") === (m.agg || "sum")) || govMeas.find((x) => x.field === m.field);
        return gm ? { key: gm.key, field: gm.field || "", agg: gm.agg || "sum" } : m;
      });
      const dimensions = (config.dimensions || []).map((d) => {
        if (d.key) return d;
        const gd = dimByField[d.field];
        return gd ? { key: gd.key, field: gd.field, date_part: d.date_part || "none" } : d;
      });
      patchConfig({ adhoc: false, measures, dimensions });
    }
  };

  const addDrill = () => {
    const usedFields = new Set([...(config.dimensions || []).map((d) => d.field), ...(config.drilldownPath || []).map((d) => d.field)]);
    const next = dims.find((c) => !usedFields.has(c.name)) || dims[0];
    patchConfig({ drilldownPath: [...(config.drilldownPath || []), { field: next?.name || "", date_part: "none" }] });
  };
  const updateDrill = (i, k, v) => { const arr = [...(config.drilldownPath || [])]; arr[i] = { ...arr[i], [k]: v }; patchConfig({ drilldownPath: arr }); };
  const removeDrill = (i) => { const arr = [...(config.drilldownPath || [])]; arr.splice(i, 1); patchConfig({ drilldownPath: arr }); };

  const fieldType = (name) => { const t = effType(name); return isDateType(t) ? "datetime" : t; };

  const allowDrop = (e) => { if (e.dataTransfer?.types?.includes(FIELD_MIME)) e.preventDefault(); };
  const onDropDim = (e) => {
    const p = readFieldDrag(e);
    if (!p || p.kind === "measure") return;
    e.preventDefault(); e.stopPropagation();
    if (widget.dataset_id && widget.dataset_id !== p.dataset_id) {
      if (p.kind === "hierarchy" || !p.field) return;
      patchConfig({ adhoc: true, dimensions: [...(config.dimensions || []), { field: p.field, date_part: p.date_part || "none", dataset_id: p.dataset_id }] });
      return;
    }
    patchConfig({ dimensions: [...(config.dimensions || []), { field: p.field || (p.levels?.[0]?.field ?? ""), date_part: p.date_part || "none" }] });
  };
  const onDropMeas = (e) => {
    const p = readFieldDrag(e);
    if (!p) return;
    e.preventDefault(); e.stopPropagation();
    if (widget.dataset_id && widget.dataset_id !== p.dataset_id) {
      if (!p.field) return;
      const agg = p.kind === "measure" ? (p.agg || "sum") : ((p.default_agg && p.default_agg !== "none") ? p.default_agg : "sum");
      patchConfig({ adhoc: true, measures: [...(config.measures || []), { field: p.field, agg, dataset_id: p.dataset_id }] });
      return;
    }
    if (p.kind === "measure") patchConfig({ measures: [...(config.measures || []), { key: p.key, field: p.field || "", agg: p.agg || "sum" }] });
    else {
      const agg = p.default_agg || defaultAggFor(p.field, schema, fmeta);
      patchConfig({ measures: [...(config.measures || []), { field: p.field, agg: agg === "none" ? "sum" : agg }] });
    }
  };

  const showStyle = !NO_STYLE.includes(widget.type);
  const isText = widget.type === "text";
  const isSlicer = widget.type === "slicer";
  const isImageWidget = widget.type === "image";
  const isMultiFilter = widget.type === "multi_filter";
  const isDataGrid = widget.type === "data_grid";
  const isContainer = widget.type === "container";
  const isFilterWidget = ["combo_box", "list_box", "date_picker"].includes(widget.type);
  const contentOnly = isText || isImageWidget || isContainer;
  const filterLike = isSlicer || isMultiFilter || isFilterWidget;
  const hasDataSection = !contentOnly;
  const hasInteractions = !contentOnly && !filterLike && !isDataGrid && widget.dataset_id;
  const showCondFormat = ["kpi", "table", "pivot"].includes(widget.type) && widget.dataset_id;
  const hasCondSection = showCondFormat || (showStyle && ADV_COLOR_TYPES.includes(widget.type));
  const isDateDim = fieldType(config.dimensions?.[0]?.field) === "datetime" || !!(config.dimensions?.[0]?.date_part && config.dimensions[0].date_part !== "none");

  // Per-widget "reset formatting to defaults" — keeps data assignment, wipes style.
  const resetFormatting = () => {
    const keep = {};
    ["subtitle", "altText", "compare"].forEach((k) => { if (opts[k] !== undefined) keep[k] = opts[k]; });
    patchConfig({ options: keep });
    toast.success("Formatting reset to defaults");
  };

  // Switch a widget's bound source: same-named fields are kept, others remapped by name.
  const changeSource = (dsId) => {
    if (!dsId || dsId === widget.dataset_id) return;
    const dest = datasets.find((d) => d.id === dsId);
    if (!dest) return;
    const map = autoFieldMap(widgetUsedFields(widget), dest.schema || []);
    const rew = remapWidgetDataset(widget, dsId, map);
    onUpdate(widget.id, { dataset_id: dsId, config: rew.config });
    toast.success(`Source switched to ${dest.name}`);
  };

  const ins = {
    widget, config, opts, datasets, schema, isText, isSlicer, patch, patchConfig, patchOpts,
    canGovern, governed, setGovernedMode, isGeo, isPointMap, isGoogleMap, dims, disp, wells,
    govDims, govMeas, dimByKey, dimByField, measureFields, numericFields, fieldType,
    addDimension, updateDimension, updateDimensionKey, removeDimension,
    addMeasure, updateMeasure, updateMeasureKey, removeMeasure,
    allowDrop, onDropDim, onDropMeas,
    showStyle, is3d, isImage, hasAxisEditor, showCondFormat, hasDataSection,
    isMultiFilter, isDataGrid, isDateDim, isFilterWidget, isContainer,
    sourceDatasets: sourceDatasets || datasets, changeSource, cols, dashboardFilters,
  };

  const showChartStyleSection = showStyle || ["kpi", "table", "pivot", "image"].includes(widget.type);

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 pr-9 border-b border-[color:var(--glass-border)] flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0"><meta.icon className="w-4 h-4 shrink-0 text-[color:var(--brand-ocean)]" /><span className="font-display text-sm text-[color:var(--ink)] truncate">{meta.label}</span></div>
        <div className="flex items-center gap-0.5 shrink-0">
          <Button data-testid="inspector-reset-formatting" size="icon" variant="ghost" title="Reset formatting to defaults (keeps data)"
            onClick={resetFormatting} className="h-7 w-7 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><RotateCcw className="w-4 h-4" /></Button>
          <Button data-testid="inspector-delete" size="icon" variant="ghost" onClick={() => onDelete(widget.id)} className="h-7 w-7 text-[color:var(--danger)]"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <Accordion type="multiple" key={widget.type === "kpi" ? "kpi" : "std"} defaultValue={widget.type === "kpi" ? ["general", "data", "container", "interactions", "chartstyle"] : ["general", "data", "container", "interactions"]} className="pl-4 pr-6 pb-6">
          {/* Assigned Data (item 187) */}
          {hasDataSection && <InspectorDataSection ins={ins} />}
          {/* Combine Widget (container): layout mode, spacing, children management */}
          {isContainer && <InspectorContainerSection ins={ins} widgets={widgets} onReleaseChild={onReleaseChild} onReorderChildren={onReorderChildren} onChildLayout={onChildLayout} onUpdateWidget={onUpdateWidget} />}
          {/* 14 standardized property sections (item 188) */}
          <InspectorGeneralSection ins={ins} />
          <InspectorLayoutSection ins={ins} />
          <InspectorTypographySection ins={ins} />
          <InspectorBackgroundSection ins={ins} />
          {showStyle && <InspectorColorsSection ins={ins} />}
          {showStyle && <InspectorLabelsSection ins={ins} />}
          {showStyle && <InspectorLegendSection ins={ins} />}
          {showStyle && (hasAxisEditor || (!isGeo && !is3d)) && <InspectorAxesSection ins={ins} />}
          {showStyle && widget.dataset_id && <InspectorTooltipSection ins={ins} />}
          {showChartStyleSection && <InspectorChartStyleSection ins={ins} />}
          {widget.dataset_id && !filterLike && !contentOnly && <InspectorNumberFormatSection ins={ins} />}
          {hasCondSection && <InspectorCondFormatSection ins={ins} />}

          {/* Interactions & Accessibility (item 191) */}
          {hasInteractions && (
            <Section value="interactions" icon={MousePointerClick} title="Interactions & Accessibility" testid="inspector-sec-interactions"
              hint="Cross-filtering, drilldowns, click actions and screen-reader text.">
              <StyleToggle label="Cross-filter other widgets on click" testid="crossfilter-toggle" checked={config.cross_filter !== false} onChange={(v) => patchConfig({ cross_filter: v })} />
              {HIGHLIGHT_TYPES.includes(widget.type) && (
                <div className="flex items-center justify-between gap-2 py-1">
                  <Label className="text-sm text-[color:var(--ink-2)]">When cross-filtered by others</Label>
                  <Select value={config.cross_receive || "filter"} onValueChange={(v) => patchConfig({ cross_receive: v })}>
                    <SelectTrigger data-testid="cross-receive-mode" className="bg-[color:var(--surface)] w-[110px] h-8 text-xs shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="filter">Filter</SelectItem>
                      <SelectItem value="highlight">Highlight</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {config.cross_receive === "highlight" && (
                <p className="text-[10px] text-[color:var(--ink-3)]">Highlight keeps all data visible and dims non-matching points instead of removing them.</p>
              )}
              {DRILLDOWN_TYPES.includes(widget.type) && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-[color:var(--ink-3)] flex items-center gap-1"><Layers className="w-3.5 h-3.5" />{isGeo ? "Geo drill levels (state, city)" : "Drilldown levels"}</Label>
                    <Button data-testid="add-drilldown" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={addDrill}><Plus className="w-3.5 h-3.5" /></Button>
                  </div>
                  <p className="text-[11px] text-[color:var(--ink-3)] mt-1">{isGeo ? "Click a country to drill into its states, then cities." : "Click a bar/slice to drill into the next level."}</p>
                  <div className="space-y-2 mt-1.5">
                    {(config.drilldownPath || []).map((d, i) => (
                      <div key={i} className="flex gap-1.5 items-center">
                        <span className="text-[11px] text-[color:var(--ink-3)] w-4 shrink-0">{i + 1}.</span>
                        <Select value={d.field} onValueChange={(v) => updateDrill(i, "field", v)}>
                          <SelectTrigger className="bg-[color:var(--surface)] flex-1 min-w-0"><SelectValue /></SelectTrigger>
                          <SelectContent>{dims.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}</SelectContent>
                        </Select>
                        {fieldType(d.field) === "datetime" && (
                          <Select value={normalizeDatePart(d.date_part)} onValueChange={(v) => updateDrill(i, "date_part", v)}>
                            <SelectTrigger className="bg-[color:var(--surface)] w-[104px] shrink-0"><SelectValue /></SelectTrigger>
                            <SelectContent>{DATE_PARTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-[color:var(--ink-3)]" onClick={() => removeDrill(i)}><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <DateHierarchyButton config={config} patchConfig={patchConfig} isDateDim={!isGeo && fieldType(config.dimensions?.[0]?.field) === "datetime"} />
                  </div>
                </div>
              )}
              <ActionEditor config={config} patchConfig={patchConfig} />
              <DrillthroughEditor widget={widget} config={config} patchConfig={patchConfig} />
              <div className="pt-2 border-t border-[color:var(--glass-border)]">
                <Label className="text-xs text-[color:var(--ink-3)] flex items-center gap-1"><Accessibility className="w-3.5 h-3.5" />Alt text (screen readers)</Label>
                <Input data-testid="inspector-alttext" value={opts.altText || ""} onChange={(e) => patchOpts({ altText: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" placeholder="Describe this visual for accessibility" />
              </div>
            </Section>
          )}

          {/* Advanced */}
          {!contentOnly && !filterLike && widget.dataset_id && (
            <Section value="advanced" icon={Settings2} title="Advanced" testid="inspector-sec-advanced"
              hint="Row limits, forecasting and performance options.">
              {!isDataGrid && (
                <div>
                  <Label className="text-xs text-[color:var(--ink-3)]">Row limit</Label>
                  <Input type="number" value={config.limit || 1000} onChange={(e) => patchConfig({ limit: Number(e.target.value) || 1000 })} className="mt-1.5 bg-[color:var(--surface)]" />
                  <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Maximum rows fetched for this widget. Lower it for faster loads on large datasets.</p>
                </div>
              )}
              {["line", "area"].includes(widget.type) && (
                <div className="pt-2 border-t border-[color:var(--glass-border)]" data-testid="forecast-editor">
                  <div className="flex items-center justify-between gap-2 py-1">
                    <Label className="text-sm text-[color:var(--ink-2)] flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />Forecast</Label>
                    <Switch data-testid="forecast-toggle" checked={!!config.forecast?.enabled}
                      onCheckedChange={(v) => patchConfig({ forecast: v ? { enabled: true, periods: 6 } : null })} className="shrink-0" />
                  </div>
                  {config.forecast?.enabled && (
                    <div className="flex items-center gap-2 mt-1">
                      <Label className="text-xs text-[color:var(--ink-3)]">Periods ahead</Label>
                      <Input data-testid="forecast-periods" type="number" min={1} max={36} value={config.forecast.periods || 6}
                        onChange={(e) => patchConfig({ forecast: { ...config.forecast, periods: Number(e.target.value) || 6 } })}
                        className="bg-[color:var(--surface)] w-20 h-8" />
                    </div>
                  )}
                  <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Holt-Winters with linear fallback. Needs exactly one date dimension bucketed by month/quarter/etc. Shown as a dashed line with a confidence band.</p>
                </div>
              )}
            </Section>
          )}
        </Accordion>
        <div className="h-6" />
      </ScrollArea>
    </div>
  );
}
